/** Calls across a message boundary (a worker, the page), each answered by its call id. */
export interface PendingCalls<T> {
    /** Sends a call under a new id; resolves with its answer, or the abandoned result if none will come. */
    call(send: (callId: number) => void): Promise<T>;
    /** The answer to call `callId`; ignored when that call isn't waiting. */
    settle(callId: number, result: T): void;
    /** Nothing will answer any more (the other side is gone): every waiting call, and any made after, gets the abandoned result. */
    abandon(): void;
}

/** Tracks calls by id until their answer comes back, or until they are abandoned with `abandoned`. */
export function createPendingCalls<T>(abandoned: T): PendingCalls<T> {
    let nextCallId = 0;
    let isAbandoned = false;
    const waiting = new Map<number, (result: T) => void>();
    return {
        call(send: (callId: number) => void): Promise<T> {
            if (isAbandoned) {
                return Promise.resolve(abandoned);
            }
            const callId = nextCallId++;
            return new Promise((resolve) => {
                waiting.set(callId, resolve);
                send(callId);
            });
        },
        settle(callId: number, result: T): void {
            waiting.get(callId)?.(result);
            waiting.delete(callId);
        },
        abandon(): void {
            isAbandoned = true;
            for (const resolve of waiting.values()) {
                resolve(abandoned);
            }
            waiting.clear();
        },
    };
}
